/**
 * Assigns columnIds to header and body cells in a table body structure.
 * This function mutates the provided body object by assigning columnIds to all cells
 * based on their column position (index within each row).
 *
 * @param body - The table body containing headerData and bodyData
 * @param body.headerData - Optional header data with rows and cells
 * @param body.bodyData - Array of body groups, each with rows and cells
 * @param columnIds - Array of columnIds to assign (one per column)
 */
export const assignColumnIdsToCells = <T extends { columnId?: string }>(
  body: {
    headerData?: false | { rows: Array<{ cells: T[] }> };
    bodyData?: Array<{
      rows: Array<{ cells: T[] }>;
    }>;
  },
  columnIds: string[],
): void => {
  if (
    !body.bodyData ||
    body.bodyData.length < 1 ||
    body.bodyData[0].rows.length < 1 ||
    body.bodyData[0].rows[0].cells.length !== columnIds.length ||
    columnIds.some((id) => !id)
  ) {
    throw new Error(`Bad columnIds array provided`);
  }
  // Assign columnIds to header cells if header exists
  if (body.headerData && typeof body.headerData === "object" && "rows" in body.headerData) {
    body.headerData.rows = body.headerData.rows.map((row) => ({
      ...row,
      cells: row.cells.map((cell, index) => ({
        ...cell,
        columnId: columnIds[index],
      })),
    }));
  }

  // Assign columnIds to body cells
  /* istanbul ignore else */
  if (body.bodyData && Array.isArray(body.bodyData)) {
    body.bodyData = body.bodyData.map((group) => ({
      ...group,
      rows: group.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell, index) => ({
          ...cell,
          columnId: columnIds[index],
        })),
      })),
    }));
  }
};
